import { useState, useEffect, useRef, useCallback } from 'react';

const CHECK_INTERVAL_MS = 2 * 60 * 1000; // Cada 2 minutos.

/**
 * Detecta deploys nuevos sin que el usuario tenga que recargar a mano.
 *
 * Al montar, fetchea /version.json y guarda su buildId como "baseline" de la
 * sesión (= versión del bundle que está corriendo, porque ambos salen del mismo
 * build). Luego repite el fetch cada 2 min y cada vez que la pestaña vuelve al
 * foco; si el buildId cambió respecto del baseline, hubo un deploy nuevo.
 *
 * - Al volver a la pestaña (focus / visibilitychange) con versión nueva: recarga
 *   directo (el usuario ya no estaba en medio de una carga).
 * - Estando en la pestaña: expone updateAvailable para mostrar el banner con
 *   cuenta regresiva y botón de recarga manual.
 */
export const useVersionCheck = () => {
    const baselineRef = useRef(null);
    const [updateAvailable, setUpdateAvailable] = useState(false);

    const reloadNow = useCallback(() => {
        window.location.reload();
    }, []);

    const fetchBuildId = useCallback(async () => {
        try {
            const response = await fetch(`/version.json?ts=${Date.now()}`, { cache: 'no-store' });
            if (!response.ok) return null;
            const data = await response.json();
            return data?.buildId ?? null;
        } catch (error) {
            // Errores de red transitorios se ignoran: reintentamos en el próximo ciclo.
            return null;
        }
    }, []);

    const check = useCallback(async ({ reloadOnMismatch = false } = {}) => {
        const buildId = await fetchBuildId();
        if (!buildId) return;

        if (baselineRef.current === null) {
            baselineRef.current = buildId;
            return;
        }

        if (buildId !== baselineRef.current) {
            if (reloadOnMismatch) {
                reloadNow();
            } else {
                setUpdateAvailable(true);
            }
        }
    }, [fetchBuildId, reloadNow]);

    useEffect(() => {
        check(); // Establece el baseline al cargar.

        const intervalId = setInterval(() => check(), CHECK_INTERVAL_MS);

        const handleVisibility = () => {
            if (document.visibilityState === 'visible') {
                check({ reloadOnMismatch: true });
            }
        };
        const handleFocus = () => check({ reloadOnMismatch: true });

        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener('focus', handleFocus);

        return () => {
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('focus', handleFocus);
        };
    }, [check]);

    return { updateAvailable, reloadNow };
};
